import { useContext, useEffect } from 'react';

import { Money, formatMoney, toMoney } from '@betterment/js-runtime';
import { Box, Button, FormikField as Field, Input, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { LegacyTransferType } from '../../../../../graphql/schema';
import {
  calculateCashflows,
  convertToMoneyWithDefault,
  hasMaskedFormFieldChanged,
} from '../../../../shared/helpers/utils';
import { ForecasterPurposeInputsContext, ProjectionInputs } from '../../../context/ForecasterPurposeInputsContext';
import { useMajorPurchaseProjection } from '../../../context/ForecasterPurposeProjectionContext';
import { MajorPurchaseSettingsPurpose } from '../../../data';
import { handleSetupOneTimeDeposit, handleSetupRecurringDeposit } from '../utils';

type Props = {
  purpose: MajorPurchaseSettingsPurpose;
};

const schema = Yup.object().shape({
  adHocInflow: Yup.string().optional(),
  monthlyRecurringDeposits: Yup.string().optional(),
});

type FormikFormProps = {
  recommendedMonthlyDeposits?: Money | null;
  recommendedOneTimeDeposit?: Money | null;
  otherCashflows?: Money | null;
  updatedFormInputs?: ProjectionInputs['majorPurchase'];
  legacySubAccountId: string;
  automaticDepositConfigId?: number | null;
};

const FormikForm = ({
  recommendedMonthlyDeposits,
  recommendedOneTimeDeposit,
  otherCashflows,
  updatedFormInputs,
  legacySubAccountId,
  automaticDepositConfigId,
}: FormikFormProps) => {
  const { handleSubmit, initialValues, setFieldValue, submitForm, validateForm, values } = useFormikContext<{
    adHocInflow?: string;
    monthlyRecurringDeposits?: string;
  }>();

  const shouldShowRecommendedMonthlyDeposits =
    recommendedMonthlyDeposits &&
    recommendedMonthlyDeposits.cents !== updatedFormInputs?.monthlyRecurringDeposits?.cents;
  const shouldShowRecommendedOneTimeDeposit =
    recommendedOneTimeDeposit && recommendedOneTimeDeposit.cents !== updatedFormInputs?.adHocInflow?.cents;

  const handleFormChange = async () => {
    await validateForm();
    handleSubmit();
  };

  useEffect(() => {
    void validateForm();
    void submitForm();
  }, []);

  const handleRecommendedMonthlyDepositsClick = async () => {
    if (shouldShowRecommendedMonthlyDeposits) {
      await setFieldValue('monthlyRecurringDeposits', formatMoney(recommendedMonthlyDeposits, 'raw'), true);
      await handleFormChange();
    }
  };

  const handleRecommendedOneTimeDepositClick = async () => {
    if (shouldShowRecommendedOneTimeDeposit) {
      await setFieldValue('adHocInflow', formatMoney(recommendedOneTimeDeposit, 'raw'), true);
      await handleFormChange();
    }
  };

  return (
    <Form>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
        <Box>
          <Field name="adHocInflow">
            <Field.Label>One-time contribution</Field.Label>
            <Field.Control>
              <Input
                mask="number"
                leadingSymbol="$"
                placeholder="0"
                aria-label="One time contribution amount"
                onChange={handleFormChange}
              />
            </Field.Control>
            <Field.ErrorMessage />
          </Field>
          {shouldShowRecommendedOneTimeDeposit && (
            <Text variant="text50" css={{ mt: 'space.50' }}>
              Recommended:{' '}
              <Button variant="textPrimary" onClick={handleRecommendedOneTimeDepositClick}>
                {formatMoney(recommendedOneTimeDeposit)}
              </Button>
            </Text>
          )}
        </Box>
        {values.adHocInflow && hasMaskedFormFieldChanged(values.adHocInflow, initialValues.adHocInflow) && (
          <SambaAutoLink to={handleSetupOneTimeDeposit(legacySubAccountId, values.adHocInflow)}>
            Continue to deposit
          </SambaAutoLink>
        )}
        <Box>
          <Field name="monthlyRecurringDeposits">
            <Field.Label>Monthly contributions</Field.Label>
            <Field.Control>
              <Input
                mask="number"
                leadingSymbol="$"
                placeholder="0"
                aria-label="Monthly contributions amount"
                onChange={handleFormChange}
              />
            </Field.Control>
            <Field.ErrorMessage />
          </Field>
          {shouldShowRecommendedMonthlyDeposits && (
            <Text variant="text50" css={{ mt: 'space.50' }}>
              Recommended:{' '}
              <Button variant="textPrimary" onClick={handleRecommendedMonthlyDepositsClick}>
                {`${formatMoney(recommendedMonthlyDeposits)}/month`}
              </Button>
            </Text>
          )}
        </Box>
        {values.monthlyRecurringDeposits &&
          hasMaskedFormFieldChanged(values.monthlyRecurringDeposits, initialValues.monthlyRecurringDeposits) && (
            <SambaAutoLink
              to={handleSetupRecurringDeposit(
                legacySubAccountId,
                values.monthlyRecurringDeposits,
                automaticDepositConfigId
              )}
            >
              Continue to deposit
            </SambaAutoLink>
          )}
        {otherCashflows && (
          <Text variant="text50">Other planned transactions: {`${formatMoney(otherCashflows)}/month`}</Text>
        )}
      </Box>
    </Form>
  );
};

export const MajorPurchaseForm = ({ purpose }: Props) => {
  const inputsContext = useContext(ForecasterPurposeInputsContext);
  const { purpose: projectionPurpose } = useMajorPurchaseProjection();

  if (!inputsContext) return null;

  const {
    projectionQueryInputs: { majorPurchase: updatedFormInputs },
    update: { majorPurchaseProjectionInput: updateInputs },
  } = inputsContext;

  const { envelope } = purpose;
  const { legalSubAccounts, monthlyNetRecurringCashflows, recurringTransactionConfigs } = envelope;

  const { initialEditableAmount, editableConfigs, otherCashflows } = calculateCashflows({
    monthlyNetRecurringCashflows: monthlyNetRecurringCashflows || toMoney(0),
    legacyTransferType: LegacyTransferType.Deposit,
    configs: recurringTransactionConfigs,
  });

  return (
    <Formik
      onSubmit={values => {
        updateInputs({
          adHocInflow: convertToMoneyWithDefault(values.adHocInflow),
          monthlyRecurringDeposits: convertToMoneyWithDefault(values.monthlyRecurringDeposits),
        });
      }}
      validationSchema={schema}
      initialValues={{
        adHocInflow: '',
        monthlyRecurringDeposits: initialEditableAmount ? formatMoney(initialEditableAmount, 'raw') : '',
      }}
    >
      <FormikForm
        recommendedMonthlyDeposits={projectionPurpose?.majorPurchaseProjection?.completion?.recommendedMonthlyDeposits}
        recommendedOneTimeDeposit={projectionPurpose?.majorPurchaseProjection?.completion?.recommendedOneTimeDeposit}
        otherCashflows={otherCashflows}
        updatedFormInputs={updatedFormInputs}
        automaticDepositConfigId={editableConfigs[0]?.legacyId}
        legacySubAccountId={legalSubAccounts[0].legacySubAccountId.toString()}
      />
    </Formik>
  );
};
